import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth-context";
import { BADGE_META, buttonStyles, fieldStyle, panelStyle, sectionTitle, subtleText, tierColor, tokens } from "../lib/ui";

export function ProfilePage() {
  const { profile, loading, logout, updateProfile } = useAuth();
  const tier = profile?.tier ?? "free";
  const [displayName, setDisplayName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [avatarUrlInput, setAvatarUrlInput] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const tierMeta = tierColor(tier);

  useEffect(() => {
    setDisplayName(profile?.display_name ?? "");
    setAvatarUrlInput(profile?.avatar_url ?? "");
  }, [profile?.display_name, profile?.avatar_url]);

  async function handleNameSave(e: React.FormEvent) {
    e.preventDefault();
    setSavingName(true);
    setError("");
    setNotice("");
    try {
      await updateProfile({ display_name: displayName.trim() });
      setEditingName(false);
      setNotice("Profile name updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile name");
    } finally {
      setSavingName(false);
    }
  }

  async function saveAvatar(avatarUrl: string) {
    setSavingAvatar(true);
    setError("");
    setNotice("");
    try {
      await updateProfile({ avatar_url: avatarUrl });
      setAvatarUrlInput(avatarUrl);
      setNotice("Profile image updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile image");
    } finally {
      setSavingAvatar(false);
    }
  }

  async function handleAvatarUrlSave(e: React.FormEvent) {
    e.preventDefault();
    const next = avatarUrlInput.trim();
    if (!next) {
      setError("Please enter an image URL.");
      return;
    }
    await saveAvatar(next);
  }

  async function handleAvatarFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("Please use an image smaller than 2MB.");
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(new Error("Unable to read selected image."));
      reader.readAsDataURL(file);
    });

    await saveAvatar(dataUrl);
    e.currentTarget.value = "";
  }

  if (loading) {
    return (
      <div className="page-loading">
        <p style={subtleText}>Loading profile...</p>
      </div>
    );
  }

  const avatarLabel = (profile?.display_name ?? profile?.bio ?? "?")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="inner-page__container" style={{ gap: tokens.space.lg }}>
      {/* Hero */}
      <section style={{ ...panelStyle, background: "linear-gradient(135deg, #f0fdf4 0%, #eff6ff 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.space.lg, flexWrap: "wrap" }}>
          {/* Avatar */}
          <div style={avatarStyle}>
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="Avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: tokens.radius.full }}
              />
            ) : (
              <span style={{ fontSize: tokens.fontSize["2xl"], fontWeight: tokens.fontWeight.bold, color: tokens.color.accent }}>
                {avatarLabel}
              </span>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.sm, flexWrap: "wrap", marginBottom: "0.35rem" }}>
              <h2 style={{ margin: 0, fontSize: tokens.fontSize["2xl"] }}>
                {profile?.display_name ?? "Spiritual Seeker"}
              </h2>
              <span
                style={{
                  borderRadius: tokens.radius.full,
                  padding: "0.2rem 0.7rem",
                  fontSize: tokens.fontSize.xs,
                  fontWeight: tokens.fontWeight.bold,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  background: tierMeta.background,
                  color: tierMeta.color,
                  border: `1px solid ${tierMeta.border}`,
                }}
              >
                {tier}
              </span>
            </div>
            {profile?.bio && <p style={{ ...subtleText, marginBottom: "0.5rem" }}>{profile.bio}</p>}
            <div style={{ display: "flex", gap: tokens.space.md, flexWrap: "wrap" }}>
              <StatChip label="Streak" value={`${profile?.streak_count ?? 0} days`} />
              {profile?.membership_expires_at && (
                <StatChip label="Membership until" value={new Date(profile.membership_expires_at).toLocaleDateString()} />
              )}
            </div>
          </div>
        </div>
      </section>

      {notice && (
        <p style={{ color: tokens.color.success, background: "#ecfdf5", borderRadius: tokens.radius.md, padding: tokens.space.sm }}>
          {notice}
        </p>
      )}

      {error && (
        <p style={{ color: tokens.color.error, background: "#fef2f2", borderRadius: tokens.radius.md, padding: tokens.space.sm }}>
          {error}
        </p>
      )}

      {/* Platform access */}
      <section style={panelStyle}>
        <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>Platform Access</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: tokens.space.sm }}>
          <PlatformCard
            name="Aekam"
            description="Spiritual content, teachings, and live events"
            active={profile?.platform_access?.waytomoksha ?? false}
          />
          <PlatformCard
            name="AekamPass"
            description="Physical attendance passes and QR check-in"
            active={profile?.platform_access?.mokshapass ?? false}
          />
        </div>
      </section>

      {/* Editable profile form */}
      <section style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: tokens.space.sm }}>
          <h3 style={sectionTitle}>Account Details</h3>
          {!editingName && (
            <button onClick={() => { setDisplayName(profile?.display_name ?? ""); setEditingName(true); }} style={buttonStyles.secondary}>
              Edit
            </button>
          )}
        </div>

        {editingName ? (
          <form onSubmit={(e) => void handleNameSave(e)} style={{ display: "grid", gap: tokens.space.sm }}>
            <label style={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm }}>Display Name</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
              style={fieldStyle}
            />
            <div style={{ display: "flex", gap: tokens.space.sm }}>
              <button type="submit" disabled={savingName} style={buttonStyles.primary}>
                {savingName ? "Saving..." : "Save changes"}
              </button>
              <button type="button" onClick={() => setEditingName(false)} style={buttonStyles.secondary}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div style={{ display: "grid", gap: tokens.space.sm }}>
            <InfoRow label="Display Name" value={profile?.display_name ?? "—"} />
          </div>
        )}

        <div style={{ marginTop: tokens.space.md, paddingTop: tokens.space.md, borderTop: `1px solid ${tokens.color.border}` }}>
          <h4 style={{ marginTop: 0, marginBottom: tokens.space.xs }}>Profile Image</h4>
          <p style={{ ...subtleText, marginBottom: tokens.space.sm }}>
            Upload a photo, paste an image URL, or choose an icon avatar.
          </p>

          <form onSubmit={(e) => void handleAvatarUrlSave(e)} style={{ display: "grid", gap: tokens.space.sm }}>
            <input
              value={avatarUrlInput}
              onChange={(e) => setAvatarUrlInput(e.target.value)}
              placeholder="https://example.com/avatar.jpg"
              style={fieldStyle}
            />
            <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
              <button type="submit" disabled={savingAvatar} style={buttonStyles.secondary}>
                {savingAvatar ? "Updating..." : "Use image URL"}
              </button>
              <label style={{ ...buttonStyles.secondary, cursor: savingAvatar ? "not-allowed" : "pointer", opacity: savingAvatar ? 0.7 : 1 }}>
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => void handleAvatarFileChange(e)}
                  disabled={savingAvatar}
                />
              </label>
            </div>
          </form>

          <div style={{ display: "grid", gap: tokens.space.xs, marginTop: tokens.space.sm }}>
            <span style={{ fontSize: tokens.fontSize.sm, fontWeight: tokens.fontWeight.semibold }}>Icon avatars</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: tokens.space.xs }}>
              {ICON_AVATARS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: tokens.radius.full,
                    border: `1px solid ${tokens.color.border}`,
                    background: "#fff",
                    fontSize: "1.1rem",
                    cursor: savingAvatar ? "not-allowed" : "pointer",
                  }}
                  onClick={() => void saveAvatar(emojiToDataUri(icon))}
                  disabled={savingAvatar}
                  aria-label={`Use ${icon} as avatar`}
                  title={`Use ${icon} avatar`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Preferences */}
      {profile?.preferences && (
        <section style={panelStyle}>
          <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>Preferences</h3>
          <div style={{ display: "grid", gap: tokens.space.xs }}>
            {profile.preferences.theme && <InfoRow label="Theme" value={profile.preferences.theme} />}
            {profile.preferences.language && <InfoRow label="Language" value={profile.preferences.language} />}
            {profile.preferences.last_platform && <InfoRow label="Last Platform" value={profile.preferences.last_platform} />}
          </div>
        </section>
      )}

      {/* Badge preview */}
      <section style={panelStyle}>
        <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>Quick Badge Preview</h3>
        <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
          {Object.entries(BADGE_META).slice(0, 4).map(([key, meta]) => (
            <span
              key={key}
              title={meta.description}
              style={{
                background: `${meta.color}18`,
                border: `1px solid ${meta.color}40`,
                borderRadius: tokens.radius.full,
                padding: "0.3rem 0.75rem",
                fontSize: tokens.fontSize.sm,
                display: "inline-flex",
                alignItems: "center",
                gap: "0.35rem",
              }}
            >
              {meta.emoji} {meta.label}
            </span>
          ))}
        </div>
      </section>

      {/* Sign out */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => void logout()} style={buttonStyles.danger}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: tokens.fontSize.sm, color: tokens.color.muted }}>
      <span style={{ fontWeight: tokens.fontWeight.semibold, color: "#111827" }}>{value}</span>{" "}
      {label}
    </div>
  );
}

function PlatformCard({ name, description, active }: { name: string; description: string; active: boolean }) {
  return (
    <div
      style={{
        borderRadius: tokens.radius.md,
        padding: tokens.space.sm,
        border: `1px solid ${active ? "#86efac" : tokens.color.border}`,
        background: active ? tokens.color.accentLight : tokens.color.mutedLight,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: tokens.space.xs, marginBottom: "0.3rem" }}>
        <span style={{ fontWeight: tokens.fontWeight.bold }}>{name}</span>
        <span
          style={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.bold,
            padding: "0.15rem 0.55rem",
            borderRadius: tokens.radius.full,
            background: active ? "#dcfce7" : "#f3f4f6",
            color: active ? tokens.color.success : tokens.color.muted,
          }}
        >
          {active ? "Active" : "No access"}
        </span>
      </div>
      <p style={{ ...subtleText, fontSize: tokens.fontSize.xs }}>{description}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: tokens.space.sm, padding: "0.6rem 0", borderBottom: `1px solid ${tokens.color.border}` }}>
      <span style={{ color: tokens.color.muted, fontSize: tokens.fontSize.sm }}>{label}</span>
      <span style={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm, textAlign: "right" }}>{value}</span>
    </div>
  );
}

const avatarStyle: React.CSSProperties = {
  width: 80,
  height: 80,
  borderRadius: tokens.radius.full,
  background: "#dcfce7",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  overflow: "hidden",
  border: `3px solid ${tokens.color.accent}30`,
};

const ICON_AVATARS = ["🕉️", "🌿", "✨", "🧘", "🔥", "🌙", "🙏", "💫"];

function emojiToDataUri(emoji: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="42">${emoji}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
